import { Fragment, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Card, Typography } from '@mui/material';

import {
  CircuitOffer,
  CircuitPriceLabel,
  circuitDuration,
  circuitPriceLabels,
  departureDateLabel,
  departureMonth,
  monthLabel,
} from '../circuits';
import { formatMoney } from '../money';
import { PriceBlock } from './PriceBlock';

interface Props {
  departures: CircuitOffer[];
  /** Pasajeros de la búsqueda: es sobre cuántos se totaliza un precio `total`. */
  searchedPax: number;
  /** La generación de resultados vigente habilita reservar (chrome de M1). */
  canSelect: boolean;
  onBook: (offer: CircuitOffer) => void;
}

/**
 * Rótulo del mes. Va en monoespaciada chica: es un separador de la lista, no un
 * título que compita con las fechas.
 */
function MonthLabel({ children }: { children: ReactNode }) {
  return (
    <Typography
      component="p"
      sx={{
        fontFamily: 'monospace',
        fontSize: 10.5,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: 'text.secondary',
        pt: 1.25,
        pb: 0.25,
        pl: 1.25,
      }}
    >
      {children}
    </Typography>
  );
}

/**
 * Escribe el rótulo que decidió `circuitPriceLabels`. La decisión —qué es el
 * número grande y qué el unitario— vive en `circuits.ts` con sus tests; acá
 * solo se traduce.
 */
function usePriceLabel() {
  const { t } = useTranslation();
  return (label: CircuitPriceLabel) => {
    if (!label) return undefined;
    if (label.key === 'perPersonDouble') {
      return t('travelServices.circuits.perPersonDouble');
    }
    return t('travelServices.circuits.totalForPax', { count: label.count });
  };
}

/** Una salida: fecha, serie, precio y el botón. */
function DepartureRow({
  offer,
  searchedPax,
  canSelect,
  onBook,
}: {
  offer: CircuitOffer;
  searchedPax: number;
  canSelect: boolean;
  onBook: () => void;
}) {
  const { t, i18n } = useTranslation();
  const days = circuitDuration(offer);
  const write = usePriceLabel();
  const labels = circuitPriceLabels(offer, searchedPax);

  return (
    <Box
      display="grid"
      gridTemplateColumns={{
        xs: 'minmax(0, 1fr)',
        // El wireframe dibuja 140/175 con los rótulos en español; el inglés
        // ("returns Fri, Sep 18", "Net: EUR 768,00") necesita un poco más de
        // ancho para no partir cada renglón en dos.
        md: '175px minmax(0, 1fr) 240px 104px',
      }}
      gap={1.75}
      alignItems="center"
      py={1}
      pl={1.25}
      sx={{ '& + &': { borderTop: 1, borderColor: 'divider' } }}
    >
      <Box minWidth={0}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.3 }}>
          {departureDateLabel(offer.startDate, i18n.language)}
        </Typography>
        {/* Vuelta y duración en el mismo renglón chico: son el "hasta cuándo"
            de la misma fecha, no dos hechos separados. Sin `endDate` (el
            proveedor no siempre lo publica) queda solo la duración, y sin
            ninguno de los dos el renglón no existe. */}
        {(offer.endDate || days) && (
          <Typography color="text.secondary" sx={{ fontSize: 11.5, lineHeight: 1.35 }}>
            {[
              offer.endDate &&
                t('travelServices.circuits.returns', {
                  date: departureDateLabel(offer.endDate, i18n.language),
                }),
              days && t('travelServices.durationDays', { count: days }),
            ]
              .filter(Boolean)
              .join(' · ')}
          </Typography>
        )}
      </Box>

      <Box display="flex" gap={0.75} flexWrap="wrap" alignItems="center">
        {/* La serie es la línea de producto del operador ("Masincluido"): texto
            quieto, no un chip — el chip acá competiría con el único aviso que
            sí importa por fila. */}
        {offer.category && (
          <Typography color="text.secondary" sx={{ fontSize: 12 }}>
            {t('travelServices.circuits.series', { name: offer.category })}
          </Typography>
        )}
        {/* Lo ÚNICO que el contrato publica por salida como aviso. No hay cupo
            ni "garantizada" por fecha: esos son del producto y viven arriba. */}
        {offer.priceIsIndicative && (
          <Typography
            component="span"
            // El aviso es corto porque va por fila; el POR QUÉ (el total exacto
            // lo confirma el proveedor al reservar) queda a un hover, no
            // repetido veinte veces abajo de cada precio.
            title={t('travelServices.circuits.indicativeNotice')}
            sx={{
              fontSize: 11,
              fontWeight: 600,
              color: 'warning.dark',
              bgcolor: 'warning.light',
              borderRadius: 0.75,
              px: 0.75,
              py: 0.125,
            }}
          >
            {t('travelServices.circuits.indicativeFrom')}
          </Typography>
        )}
      </Box>

      <PriceBlock
        pricing={offer.pricing}
        unit={write(labels.total)}
        fromLabel={
          offer.priceIsIndicative ? t('travelServices.priceFrom') : undefined
        }
        // Un fragmento vacío es `truthy`: sin esto, una salida sin unitario ni
        // suplemento se llevaba un renglón en blanco de la columna.
        notes={
          (offer.unitPrice || offer.singleSupplement) && (
            <>
              {/* El unitario del que sale un precio ya totalizado: sin esto, un
                `total` por dos pasajeros no dice cuánto es por persona. */}
              {offer.unitPrice && (
                <Box component="span" display="block">
                  {[formatMoney(offer.unitPrice), write(labels.unit)]
                    .filter(Boolean)
                    .join(' ')}
                </Box>
              )}
              {/* El suplemento de individual es de CATÁLOGO: el contrato lo
                declara indicativo aunque el precio de la salida no lo sea, así
                que el rótulo va siempre. */}
              {offer.singleSupplement && (
                <Box component="span" display="block">
                  {t('travelServices.circuits.singleSupplementIndicative', {
                    price: formatMoney(offer.singleSupplement),
                  })}
                </Box>
              )}
            </>
          )
        }
      />

      <Box textAlign={{ xs: 'left', md: 'right' }}>
        <Button
          size="small"
          variant="contained"
          disabled={!canSelect}
          onClick={onBook}
          sx={{ minWidth: 96 }}
        >
          {t('travelServices.book')}
        </Button>
      </Box>
    </Box>
  );
}

/**
 * Las salidas del circuito elegido: una lista DENSA agrupada por mes, no una
 * card por fecha. Todas las filas son del mismo producto (la búsqueda es
 * dirigida), así que lo del producto vive arriba —en `CircuitProductHeader`— y
 * cada fila trae solo lo que la SALIDA publica: fecha, vuelta, duración, serie,
 * precio y el aviso de indicativo. Veinte salidas del mismo circuito en veinte
 * cards repetían el nombre veinte veces y no dejaban comparar fechas.
 *
 * El rótulo del mes aparece cuando cambia respecto de la fila anterior: con el
 * orden por fecha (el default) queda un encabezado por mes, y con el orden por
 * precio sigue diciendo la verdad en vez de agrupar lo que no está junto.
 */
export function CircuitDepartures({
  departures,
  searchedPax,
  canSelect,
  onBook,
}: Props) {
  const { i18n } = useTranslation();

  return (
    <Card variant="outlined" sx={{ px: 1.75, pb: 1 }}>
      {departures.map((offer, index) => {
        const month = departureMonth(offer);
        const previous = index > 0 ? departureMonth(departures[index - 1]) : '';
        const isNewMonth = Boolean(month) && month !== previous;
        return (
          <Fragment key={`${offer.provider}-${offer.startDate}-${index}`}>
            {isNewMonth && (
              <MonthLabel>{monthLabel(month, i18n.language)}</MonthLabel>
            )}
            <DepartureRow
              offer={offer}
              searchedPax={searchedPax}
              canSelect={canSelect}
              onBook={() => onBook(offer)}
            />
          </Fragment>
        );
      })}
    </Card>
  );
}
